import { Booking } from "../../../../../../types/bookings";
import { DecimalUtils } from "../../../../../libs/utils";
import { useTorRules } from "../../../hooks/useTorRules";
import { useSaveBillingSchedule } from "./api";
import { buildCommercialBillingScheduleBreakdownPayload } from "./buildCommercialBillingScheduleBreakdownPayload";
import type { PlanFieldItem } from "./year-card/YearCard";

type SaveBillingScheduleOptions = {
  plans: PlanFieldItem[];
  billingCharges: Booking["bookingTerms"]["billingCharges"];
  depositPayments: Booking["depositPayments"];
};

type UseSaveBillingScheduleForPlansOptions = {
  leaseId: string;
};

export const useSaveBillingScheduleForPlans = (options: UseSaveBillingScheduleForPlansOptions) => {
  const { leaseId } = options;

  const { saveBillingSchedule } = useSaveBillingSchedule();
  const { isTurnoverRentOnly } = useTorRules();

  const handleSaveBillingSchedule = async (options: SaveBillingScheduleOptions) => {
    const { plans, billingCharges, depositPayments } = options;

    const hasAnyValue = (...values: unknown[]) =>
      values.some((value) => value !== null && value !== undefined && value !== "");

    const hasPaymentPlanInputValues = (plan: PlanFieldItem) =>
      hasAnyValue(
        plan?.installments?.value,
        plan?.rentType?.value,
        plan?.annualRent,
        plan?.rentValue,
        plan?.torPeriod?.value,
        plan?.unitAreaInSqft,
        plan?.securityDeposit,
      );

    const hasServiceChargeInputValues = (plan: PlanFieldItem) =>
      hasAnyValue(
        plan?.serviceCharge?.installments?.value,
        plan?.serviceCharge?.rentType?.value,
        plan?.serviceCharge?.annualRent,
        plan?.serviceCharge?.chargeValue,
      );

    for (const plan of plans) {
      const yearLabel = `Year ${plan?.noOfYear ?? "?"}`;
      const hasBillingBreakdown = Boolean(plan?.billingScheduleRows?.length);
      const hasServiceChargeBreakdown = Boolean(plan?.serviceChargeSchedule?.length);

      if (hasPaymentPlanInputValues(plan) && !hasBillingBreakdown) {
        throw new Error(`${yearLabel}: Please generate the payment plan breakdown before proceeding`);
      }

      if (hasServiceChargeInputValues(plan) && !hasServiceChargeBreakdown) {
        throw new Error(`${yearLabel}: Please generate the service charge breakdown before proceeding`);
      }
    }

    const commercialBillingScheduleBreakdown = buildCommercialBillingScheduleBreakdownPayload(plans);

    for (const plan of plans) {
      if (!plan.billingScheduleRows?.length) continue;

      const yearLabel = `Year ${plan.billingScheduleRows[0]?.noOfYear ?? "?"}`;

      const missingInstallment = !isTurnoverRentOnly && plan.billingScheduleRows.some((row) => row.installment == null);
      if (missingInstallment) {
        throw new Error(`${yearLabel}: All installment numbers must be assigned before proceeding`);
      }

      const hasTorPeriodColumn = plan.billingScheduleRows.some((row) => "torPeriod" in row);
      if (hasTorPeriodColumn) {
        const missingTorPeriod = plan.billingScheduleRows.some((row) => row.torPeriod == null);
        if (missingTorPeriod) {
          throw new Error(`${yearLabel}: All TOR period numbers must be assigned before proceeding`);
        }
      }
    }

    if (commercialBillingScheduleBreakdown.length === 0) {
      throw new Error("Please generate the billing schedule breakdown before proceeding");
    }

    const commercialPaymentPlan = plans.map((plan) => ({
      annualRent: DecimalUtils.toNumber(plan?.annualRent),
      installments: plan?.installments?.value,
      noOfYear: plan?.noOfYear,
      rentType: plan?.rentType?.value,
      ...(plan?.rentValue ? { rentValue: DecimalUtils.toNumber(plan?.rentValue) } : {}),
      securityDeposit: DecimalUtils.toNumber(plan?.securityDeposit),
      torPeriod: plan?.torPeriod?.value,
    }));

    return saveBillingSchedule({
      leaseId,
      commercialPaymentPlan,
      commercialBillingScheduleBreakdown,
      ...(billingCharges?.length > 0 ? { bookingTerms: { billingCharges } } : {}),
      ...(depositPayments?.length > 0 ? { depositPayments } : {}),
    });
  };

  return { handleSaveBillingSchedule };
};
